import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';
import { LOST_FOUND_STATUS } from './lostAndFoundData';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || 'https://api.innoworx.ai/Stage';

const SITE_STORAGE_KEY = 'innoworx_lost_found_site_id';

const getAuthHeaders = async () => {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
};

const parseError = async response => {
  let message = `HTTP ${response.status}: ${response.statusText}`;
  try {
    const data = await response.json();
    message = data.error || data.message || message;
  } catch {
    try {
      const text = await response.text();
      if (text) message = text;
    } catch {
      /* ignore */
    }
  }
  const err = new Error(message);
  err.status = response.status;
  return err;
};

const request = async (endpoint, options = {}) => {
  const headers = await getAuthHeaders();
  const url = `${API_BASE_URL}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers: { ...headers, ...options.headers },
  });

  if (!response.ok) {
    const err = await parseError(response);
    err.endpoint = endpoint;
    if (err.status !== 404) {
      loggingService
        .logError(
          'api_error_lost_and_found',
          `Lost & Found API error: ${err.status} for ${endpoint}`,
          err,
          { endpoint, method: options.method || 'GET', status: err.status }
        )
        .catch(() => {});
    }
    throw err;
  }

  if (response.status === 204) return null;

  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
};

const requireSiteId = siteId => {
  const id = siteId || getLostFoundSiteId();
  if (!id) {
    throw new Error('Select a site before continuing.');
  }
  return id;
};

/** Map API record to UI shape used by screens */
export const mapApiItemToUi = apiItem => {
  if (!apiItem) return null;

  const apiStatus = String(apiItem.status ?? '').toUpperCase();
  const entryType = String(apiItem.entry_type ?? '').toUpperCase();

  let uiStatus = LOST_FOUND_STATUS.LOST;
  if (apiStatus === 'COLLECTED') {
    uiStatus = LOST_FOUND_STATUS.COLLECTED;
  } else if (entryType === 'FOUND') {
    uiStatus = LOST_FOUND_STATUS.FOUND;
  }

  const collectedAt = apiItem.collected_at || apiItem.collection?.collected_at;
  const hasCollection =
    apiStatus === 'COLLECTED' ||
    apiItem.collector_name ||
    apiItem.collection?.collector_name;

  return {
    id: apiItem.item_id || apiItem.id,
    item_id: apiItem.item_id || apiItem.id,
    site_id: apiItem.site_id,
    item_name: apiItem.item_name,
    entry_type: entryType,
    status: uiStatus,
    location: apiItem.location_at ?? apiItem.location ?? '',
    submit_collect_at: apiItem.handover_location ?? apiItem.submit_collect_at ?? '',
    description: apiItem.description ?? '',
    image_url: apiItem.image_url ?? null,
    image_s3_key: apiItem.image_s3_key ?? null,
    date:
      apiItem.created_at?.split?.('T')?.[0] ||
      apiItem.date ||
      apiItem.created_at ||
      '',
    collection: hasCollection
      ? (() => {
          const blockId =
            apiItem.collector_block_id ||
            apiItem.collection?.collector_block_id ||
            apiItem.collection?.block_id ||
            '';
          const flatId =
            apiItem.collector_flat_id ||
            apiItem.collection?.collector_flat_id ||
            apiItem.collection?.flat_id ||
            '';
          const blockName =
            apiItem.collector_block_name ||
            apiItem.collection?.collector_block_name ||
            apiItem.collection?.block_name ||
            '';
          const flatName =
            apiItem.collector_flat_name ||
            apiItem.collector_flat_no ||
            apiItem.collector_flat_number ||
            apiItem.collection?.collector_flat_name ||
            apiItem.collection?.flat_name ||
            apiItem.collection?.flat_no ||
            apiItem.collection?.flat_number ||
            '';
          return {
            person_name:
              apiItem.collector_name ||
              apiItem.collection?.person_name ||
              apiItem.collection?.collector_name ||
              '',
            mobile_number:
              apiItem.collector_mobile ||
              apiItem.collection?.mobile_number ||
              apiItem.collection?.collector_mobile ||
              '',
            person_photo_url:
              apiItem.collector_photo_url ||
              apiItem.collection?.person_photo_url ||
              null,
            block_id: blockId,
            flat_id: flatId,
            block: blockName,
            flat: flatName,
            description:
              apiItem.collection_notes ||
              apiItem.collection?.description ||
              apiItem.collection?.collection_notes ||
              '',
            collected_at: collectedAt || '',
          };
        })()
      : null,
  };
};

const uiStatusToEntryType = uiStatus =>
  uiStatus === LOST_FOUND_STATUS.FOUND ? 'FOUND' : 'LOST';

const mapUiPayloadToApi = (data, siteId) => {
  const body = { site_id: siteId };

  if (data.item_name != null) body.item_name = String(data.item_name).trim();
  if (data.status != null) body.entry_type = uiStatusToEntryType(data.status);
  if (data.location != null) body.location_at = String(data.location).trim();
  if (data.submit_collect_at != null) {
    body.handover_location = String(data.submit_collect_at).trim();
  }
  if (data.description != null) body.description = String(data.description).trim();
  if (data.image_s3_key != null) body.image_s3_key = data.image_s3_key;

  return body;
};

export const getLostFoundSiteId = () =>
  sessionStorage.getItem(SITE_STORAGE_KEY) || '';

export const setLostFoundSiteId = siteId => {
  if (siteId) {
    sessionStorage.setItem(SITE_STORAGE_KEY, siteId);
  } else {
    sessionStorage.removeItem(SITE_STORAGE_KEY);
  }
};

/** MIME type must match what the presign Lambda signed (default is often image/jpeg). */
export const normalizeImageContentType = (file, fallback = 'image/jpeg') => {
  const raw = String(file?.type || '').trim().toLowerCase();
  if (raw === 'image/jpg') return 'image/jpeg';
  if (raw.startsWith('image/')) return raw;
  const ext = file?.name?.split('.').pop()?.toLowerCase();
  const byExt = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    webp: 'image/webp',
    gif: 'image/gif',
    heic: 'image/heic',
    heif: 'image/heif',
  };
  return byExt[ext] || fallback;
};

const isLikelyCorsOrNetworkError = err =>
  err?.name === 'TypeError' ||
  /failed to fetch|networkerror|load failed|cors/i.test(String(err?.message ?? ''));

const uploadToPresignedPut = async (uploadUrl, file, contentType) => {
  const type = contentType || normalizeImageContentType(file);
  let response;
  try {
    response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': type },
      body: file,
    });
  } catch (err) {
    if (isLikelyCorsOrNetworkError(err)) {
      throw new Error(
        'Image upload was blocked by S3 CORS. The facility-visitor-photos bucket must allow PUT from this app origin (e.g. http://localhost:3000) with Content-Type in AllowedHeaders.'
      );
    }
    throw err;
  }
  if (!response.ok) {
    const hint =
      response.status === 403
        ? ' The Content-Type on upload must match the type sent when requesting the presign URL.'
        : '';
    throw new Error(`Image upload failed (${response.status}).${hint}`);
  }
};

/** Presigned POST avoids some PUT+CORS setups; used when the API returns presigned_post. */
const uploadToPresignedPost = async (presignedPost, file, contentType) => {
  const post = presignedPost?.presigned_post ?? presignedPost;
  const url = post?.url;
  const fields = post?.fields;
  if (!url || !fields) {
    throw new Error('Invalid presigned POST response');
  }

  const formData = new FormData();
  Object.entries(fields).forEach(([key, value]) => {
    formData.append(key, value);
  });
  const fileField =
    post.file_field || fields.file_field || (fields.key ? 'file' : 'file');
  const blob =
    file instanceof Blob
      ? file
      : new Blob([file], { type: contentType || normalizeImageContentType(file) });
  formData.append(fileField, blob, file?.name || 'upload.jpg');

  let response;
  try {
    response = await fetch(url, { method: 'POST', body: formData });
  } catch (err) {
    if (isLikelyCorsOrNetworkError(err)) {
      throw new Error(
        'Image upload was blocked by S3 CORS. The facility-visitor-photos bucket must allow POST from this app origin.'
      );
    }
    throw err;
  }
  if (!response.ok && response.status !== 204) {
    throw new Error(`Image upload failed (${response.status})`);
  }
};

const normalizeList = data => {
  const raw =
    data?.items ??
    data?.data?.items ??
    data?.data ??
    (Array.isArray(data) ? data : []);
  return Array.isArray(raw) ? raw.map(mapApiItemToUi).filter(Boolean) : [];
};

const toOption = (entry, idKeys, labelKeys) => {
  if (entry == null) return null;
  if (typeof entry === 'string' || typeof entry === 'number') {
    const value = String(entry);
    return { id: value, label: value };
  }
  if (typeof entry !== 'object') return null;

  let id;
  for (const key of idKeys) {
    if (entry[key] != null && entry[key] !== '') {
      id = String(entry[key]);
      break;
    }
  }
  if (!id) return null;

  let label = id;
  for (const key of labelKeys) {
    if (entry[key] != null && String(entry[key]).trim() !== '') {
      label = String(entry[key]);
      break;
    }
  }

  return { id, label };
};

const extractArray = (data, keys) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;

  for (const key of keys) {
    const candidate = data[key];
    if (Array.isArray(candidate)) return candidate;
    if (candidate && typeof candidate === 'object' && !Array.isArray(candidate)) {
      const nested = Object.values(candidate);
      if (nested.length && nested.every(v => typeof v === 'object' || typeof v === 'string')) {
        return nested;
      }
    }
  }

  if (data.data != null) {
    if (Array.isArray(data.data)) return data.data;
    return extractArray(data.data, keys);
  }

  return [];
};

const BLOCK_ID_KEYS = ['block_id', 'blockId', 'id', 'block_code', 'code'];
const BLOCK_LABEL_KEYS = ['block_name', 'blockName', 'name', 'label', 'block_label', 'title'];
const FLAT_ID_KEYS = ['flat_id', 'flatId', 'id', 'flat_no', 'flat_number', 'unit_id', 'unit_no'];
const FLAT_LABEL_KEYS = [
  'flat_name',
  'flatName',
  'flat_no',
  'flat_number',
  'unit_no',
  'name',
  'label',
  'title',
];

const normalizeBlocks = data => {
  const raw = extractArray(data, ['blocks', 'block_list', 'blockList', 'results', 'items']);
  return raw
    .map(b => toOption(b, BLOCK_ID_KEYS, BLOCK_LABEL_KEYS))
    .filter(Boolean);
};

const normalizeFlats = data => {
  const raw = extractArray(data, ['flats', 'flat_list', 'flatList', 'results', 'items', 'units']);
  return raw
    .map(f => toOption(f, FLAT_ID_KEYS, FLAT_LABEL_KEYS))
    .filter(Boolean);
};

/** True when value looks like an internal id/uuid, not a display label (e.g. "Block A", "102"). */
export const isLikelyOpaqueId = value => {
  const s = String(value ?? '').trim();
  if (!s) return false;
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(s)) return true;
  if (s.length > 28 && s.includes('-')) return true;
  return false;
};

const findOptionLabel = (options, id) => {
  if (!id || !options?.length) return '';
  const key = String(id);
  const match = options.find(o => String(o.id) === key);
  return match?.label || '';
};

/** Resolve collector block/flat ids to human-readable labels via facility APIs. */
export const enrichCollectionLabels = async (siteId, collection) => {
  if (!collection || !siteId) return collection;

  const blockId =
    collection.block_id ||
    (isLikelyOpaqueId(collection.block) ? String(collection.block).trim() : '');
  const flatId =
    collection.flat_id ||
    (isLikelyOpaqueId(collection.flat) ? String(collection.flat).trim() : '');

  let block =
    collection.block && !isLikelyOpaqueId(collection.block)
      ? String(collection.block).trim()
      : '';
  let flat =
    collection.flat && !isLikelyOpaqueId(collection.flat)
      ? String(collection.flat).trim()
      : '';

  try {
    if (blockId && !block) {
      const blocks = await lostAndFoundApi.getBlocks(siteId);
      block = findOptionLabel(blocks, blockId);
    }
    if (blockId && flatId && !flat) {
      const flats = await lostAndFoundApi.getFlats(siteId, blockId);
      flat = findOptionLabel(flats, flatId);
    }
  } catch {
    /* keep empty labels rather than showing raw ids */
  }

  return {
    ...collection,
    block_id: blockId,
    flat_id: flatId,
    block,
    flat,
  };
};

export const lostAndFoundApi = {
  getLostFoundSiteId,
  setLostFoundSiteId,
  mapApiItemToUi,
  enrichCollectionLabels,

  getAll: async ({
    siteId,
    statusFilter,
    search,
    limit = 25,
    nextToken,
  } = {}) => {
    const sid = requireSiteId(siteId);
    const params = new URLSearchParams({ site_id: sid });
    if (statusFilter) params.set('status', statusFilter);
    if (search?.trim()) params.set('search', search.trim());
    if (limit) params.set('limit', String(limit));
    if (nextToken) params.set('next_token', nextToken);

    const data = await request(`/community/lost-found/items?${params}`);
    return {
      items: normalizeList(data),
      nextToken: data?.next_token ?? data?.nextToken ?? null,
    };
  },

  getById: async (itemId, siteId) => {
    const sid = requireSiteId(siteId);
    const data = await request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}?site_id=${encodeURIComponent(sid)}`
    );
    const item = data?.item ?? data;
    const mapped = mapApiItemToUi(item);
    if (!mapped?.id) throw new Error('Item not found');
    return mapped;
  },

  create: async (data, siteId) => {
    const sid = requireSiteId(siteId);
    const body = mapUiPayloadToApi(data, sid);
    const result = await request('/community/lost-found/items', {
      method: 'POST',
      body: JSON.stringify(body),
    });
    return mapApiItemToUi(result?.item ?? result);
  },

  update: async (itemId, data, siteId) => {
    const sid = requireSiteId(siteId);
    const body = mapUiPayloadToApi(data, sid);
    const result = await request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(body),
      }
    );
    return mapApiItemToUi(result?.item ?? result);
  },

  delete: async (itemId, siteId) => {
    const sid = requireSiteId(siteId);
    return request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}?site_id=${encodeURIComponent(sid)}`,
      { method: 'DELETE' }
    );
  },

  collect: async (itemId, data, siteId) => {
    const sid = requireSiteId(siteId);
    const body = {
      site_id: sid,
      collector_name: String(data.person_name ?? data.collector_name ?? '').trim(),
      collector_mobile: String(data.mobile_number ?? data.collector_mobile ?? '').trim(),
      collection_notes: String(data.description ?? data.collection_notes ?? '').trim(),
    };
    if (data.collector_photo_s3_key) {
      body.collector_photo_s3_key = data.collector_photo_s3_key;
    }
    if (data.collector_block_id || data.block) {
      body.collector_block_id = data.collector_block_id || data.block;
    }
    if (data.collector_flat_id || data.flat) {
      body.collector_flat_id = data.collector_flat_id || data.flat;
    }

    const result = await request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}/collect`,
      {
        method: 'POST',
        body: JSON.stringify(body),
      }
    );
    return mapApiItemToUi(result?.item ?? result);
  },

  presignItemImage: async (itemId, siteId, contentType) => {
    const sid = requireSiteId(siteId);
    const type = contentType || 'image/jpeg';
    return request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}/image/presign`,
      {
        method: 'POST',
        body: JSON.stringify({ site_id: sid, content_type: type }),
      }
    );
  },

  presignCollectorPhoto: async (itemId, siteId, contentType) => {
    const sid = requireSiteId(siteId);
    const type = contentType || 'image/jpeg';
    return request(
      `/community/lost-found/items/${encodeURIComponent(itemId)}/collector-photo/presign`,
      {
        method: 'POST',
        body: JSON.stringify({ site_id: sid, content_type: type }),
      }
    );
  },

  uploadImageFile: async (presignResult, file) => {
    const s3Key = presignResult?.s3_key;
    if (!s3Key) {
      throw new Error('Invalid presign response: missing s3_key');
    }

    const contentType =
      presignResult?.content_type ||
      normalizeImageContentType(file);

    const postPayload =
      presignResult?.presigned_post ??
      (presignResult?.url && presignResult?.fields
        ? { url: presignResult.url, fields: presignResult.fields }
        : null);

    if (postPayload?.url && postPayload?.fields) {
      await uploadToPresignedPost(postPayload, file, contentType);
      return s3Key;
    }

    const uploadUrl = presignResult?.upload_url;
    if (!uploadUrl) {
      throw new Error('Invalid presign response: missing upload_url');
    }
    await uploadToPresignedPut(uploadUrl, file, contentType);
    return s3Key;
  },

  uploadItemImage: async (itemId, file, siteId) => {
    const contentType = normalizeImageContentType(file);
    const presign = await lostAndFoundApi.presignItemImage(itemId, siteId, contentType);
    return lostAndFoundApi.uploadImageFile(presign, file);
  },

  uploadCollectorPhoto: async (itemId, file, siteId) => {
    const contentType = normalizeImageContentType(file);
    const presign = await lostAndFoundApi.presignCollectorPhoto(
      itemId,
      siteId,
      contentType
    );
    return lostAndFoundApi.uploadImageFile(presign, file);
  },

  getBlocks: async siteId => {
    const sid = requireSiteId(siteId);
    const data = await request(
      `/facility/blocks?site_id=${encodeURIComponent(sid)}`
    );
    const blocks = normalizeBlocks(data);
    if (blocks.length > 0) return blocks;

    // Some gateways wrap the list one level deeper
    const alt = normalizeBlocks(data?.data ?? data?.result ?? data?.payload);
    return alt;
  },

  getFlats: async (siteId, blockId) => {
    const sid = requireSiteId(siteId);
    if (!blockId) return [];
    const params = new URLSearchParams({
      site_id: sid,
      block_id: String(blockId),
      block: String(blockId),
    });
    const data = await request(`/facility/flats?${params}`);
    const flats = normalizeFlats(data);
    if (flats.length > 0) return flats;
    return normalizeFlats(data?.data ?? data?.result ?? data?.payload);
  },
};
